import React, { Component } from 'react';
import lodashCompact from 'lodash/fp/compact';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import lodashValues from 'lodash/fp/values';

import {
  JOB_ORDER_STATUS_CREATED,
  JOB_ORDER_STATUS_ASSIGNED,
  JOB_ORDER_STATUS_ACCEPTED,
  JOB_ORDER_STATUS_COMPLETED,
  JOB_ORDER_STATUS_FAILED,
  JOB_ORDER_STATUS_CANCELLED,
  srJobChoices,
  DRIVER_STATUS_ACTIVE,
} from 'common/jobConstants';

import withStripe from 'client/helpers/withStripe';
import {
  createFormValidationFn,
  createInitialValues,
  hideFieldIf,
} from 'client/helpers/formikHelpers';

import SrBox from 'client/components/SrBox';
import SrButton, { SR_BUTTON_THEME_ADOBE_GREEN } from 'client/components/SrButton/SrButton';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';

const availableTransitionsForAdmin = {
  [JOB_ORDER_STATUS_CREATED]: [JOB_ORDER_STATUS_CANCELLED],
  [JOB_ORDER_STATUS_ASSIGNED]: [JOB_ORDER_STATUS_CANCELLED],
  [JOB_ORDER_STATUS_ACCEPTED]: [JOB_ORDER_STATUS_COMPLETED, JOB_ORDER_STATUS_CANCELLED],
  [JOB_ORDER_STATUS_COMPLETED]: [JOB_ORDER_STATUS_CANCELLED],
  [JOB_ORDER_STATUS_FAILED]: [JOB_ORDER_STATUS_COMPLETED],
  [JOB_ORDER_STATUS_CANCELLED]: [],
};

function _getStatusChoices(jobOrderStatus) {
  const statusChoices = [
    {
      label: jobOrderStatus,
      value: jobOrderStatus,
    },
  ];
  if (!jobOrderStatus) {
    return statusChoices;
  }
  return [
    ...statusChoices,
    ...availableTransitionsForAdmin[jobOrderStatus].map((status) => ({
      label: status,
      value: status,
    })),
  ];
}

function getFormFields({ jobOrder, driversByMarketId }) {
  const driversList = lodashValues(driversByMarketId)
    .reduce(
      (memo, driverGroup) => [
        ...memo,
        ...driverGroup.filter(
          ({ id, status }) => status === DRIVER_STATUS_ACTIVE || jobOrder?.driverId === id,
        ),
      ],
      [],
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  return lodashCompact([
    {
      id: 'listingId',
      title: 'Listing ID',
      subtitle: "We'll implement autocomplete soon...",
      type: 'number',
      validations: ['required'],
    },
    hideFieldIf(!jobOrder, {
      id: 'status',
      title: 'Status',
      type: 'select',
      validations: ['required'],
      properties: {
        choices: _getStatusChoices(jobOrder?.status),
        multipleSelection: false,
        allowEmptyArray: false,
      },
    }),
    {
      id: 'dueDate',
      title: 'Due Date',
      type: 'date_picker',
      validations: ['required'],
    },
    {
      id: 'driverId',
      title: 'Driver',
      type: 'select',
      properties: {
        choices: driversList.map((driver) => ({
          label: driver.name,
          value: driver.id,
        })),
        placeholder: 'Unassigned',
        enableResetOption: true,
        multipleSelection: false,
        allowEmptyArray: false,
        transformToNumber: true,
      },
    },
    ...srJobChoices,
    {
      id: 'instructions',
      title: 'Instructions',
      type: 'long_text',
    },
  ]);
}

@withStripe
class SrUpsertJobOrderForm extends Component {
  static propTypes = {
    onSubmit: PropTypes.func.isRequired,
    jobOrder: PropTypes.object,
    listingId: PropTypes.number,
    driversByMarketId: PropTypes.object.isRequired,
  };

  render() {
    const { onSubmit, jobOrder, listingId, driversByMarketId } = this.props;

    const initialFormFields = getFormFields({
      jobOrder,
      driversByMarketId,
    });

    return (
      <Formik
        onSubmit={onSubmit}
        initialValues={createInitialValues({
          formFields: initialFormFields,
          defaultValues:
            !jobOrder && !listingId
              ? null
              : {
                  listingId: listingId,
                  dueDate: jobOrder?.dueDate,
                  driverId: jobOrder?.driverId,
                  status: jobOrder?.status,
                  instructions: jobOrder?.instructions,
                  ...jobOrder?.jobs.reduce(
                    (memo, { jobTypeId }) => ({
                      ...memo,
                      [jobTypeId]: true,
                    }),
                    {},
                  ),
                },
        })}
        validate={createFormValidationFn(initialFormFields)}
        render={(formikBag) => {
          const formFields = getFormFields({
            jobOrder,
            driversByMarketId,
          });
          return (
            <Form>
              {formFields.map((formField, index) => (
                <SrFormikField
                  formField={formField}
                  formikBag={formikBag}
                  theme={SR_FIELD_THEME_ADOBE}
                  margin={index === 0 ? '0' : null}
                  key={formField.id}
                />
              ))}
              <SrBox paddingTop="20px">
                <SrButton type="submit" theme={SR_BUTTON_THEME_ADOBE_GREEN}>
                  Submit
                </SrButton>
              </SrBox>
            </Form>
          );
        }}
      />
    );
  }
}

SrUpsertJobOrderForm.displayName = 'SrUpsertJobOrderForm';

export default SrUpsertJobOrderForm;
